import type { LeadCardHistoryItemData } from '@app/components/LeadCard/data';
import { LeadAudioMessage } from '@app/components/LeadCard/ui/MessageHistory/components/LeadAudioMessage';
import { LeadFileAttachment } from '@app/components/LeadCard/ui/MessageHistory/components/LeadFileAttachment';
import { LeadImageAttachment } from '@app/components/LeadCard/ui/MessageHistory/components/LeadImageAttachment';
import { LeadTechnicalMessage } from '@app/components/LeadCard/ui/MessageHistory/components/LeadTechnicalMessage';
import { LeadVideoAttachment } from '@app/components/LeadCard/ui/MessageHistory/components/LeadVideoAttachment';
import type { NFiles } from '@app/types';

import type { FC } from 'react';

import './LeadFileHistoryAction.scss';

interface LeadFileHistoryActionProps {
  item: LeadCardHistoryItemData;
}

interface GroupedHistoryFiles {
  images: NFiles.ModelClientFile[];
  videos: NFiles.ModelClientFile[];
  audio: NFiles.ModelClientFile[];
  files: NFiles.ModelClientFile[];
}

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']);
const VIDEO_EXTENSIONS = new Set(['mp4']);
const AUDIO_EXTENSIONS = new Set(['mp3', 'ogg', 'wav', 'amr', 'opus']);

const getFilesFromAction = (
  item: LeadCardHistoryItemData
): NFiles.ModelClientFile[] => {
  if (item.Files && item.Files.length > 0) return item.Files;

  const firstMessageFile = item.ChatMessages?.[0]?.File ?? null;
  return firstMessageFile ? [firstMessageFile] : [];
};

const getFileExtension = (file: NFiles.ModelClientFile): string =>
  (file.Extension ?? '').trim().toLowerCase();

const isVideoFile = (file: NFiles.ModelClientFile): boolean => {
  const extension = getFileExtension(file);

  if (extension === 'webp' && file.ExternalAccessId) return true;

  return VIDEO_EXTENSIONS.has(extension) && !!file.ExternalAccessId;
};

const isImageFile = (file: NFiles.ModelClientFile): boolean => {
  if (isVideoFile(file)) return false;

  return IMAGE_EXTENSIONS.has(getFileExtension(file));
};

const isAudioFile = (file: NFiles.ModelClientFile): boolean =>
  AUDIO_EXTENSIONS.has(getFileExtension(file));

const groupHistoryFiles = (
  files: NFiles.ModelClientFile[]
): GroupedHistoryFiles =>
  files.reduce<GroupedHistoryFiles>(
    (groups, file) => {
      if (isVideoFile(file)) {
        groups.videos.push(file);
        return groups;
      }

      if (isImageFile(file)) {
        groups.images.push(file);
        return groups;
      }

      if (isAudioFile(file)) {
        groups.audio.push(file);
        return groups;
      }

      groups.files.push(file);
      return groups;
    },
    {
      images: [],
      videos: [],
      audio: [],
      files: [],
    }
  );

export const LeadFileHistoryAction: FC<LeadFileHistoryActionProps> = ({
  item,
}) => {
  const files = getFilesFromAction(item);
  const groupedFiles = groupHistoryFiles(files);

  const sharedAttachmentProps = {
    socialType: null,
    isIncoming: true,
    timestamp: item.Timestamp,
    wasEdited: false,
    status: null,
    isForwarded: false,
    replyMessage: null,
    replyMessageId: null,
    showStatus: false,
  };

  return (
    <div className="LeadFileHistoryAction">
      <LeadTechnicalMessage item={item} />

      {files.length > 0 && (
        <div className="LeadFileHistoryAction__groups">
          {(groupedFiles.images.length > 0 ||
            groupedFiles.videos.length > 0) && (
            <div className="LeadFileHistoryAction__group LeadFileHistoryAction__group--media">
              {groupedFiles.images.map((file, index) => (
                <div
                  className="LeadFileHistoryAction__item"
                  key={`image_${file.Id}_${index}`}
                >
                  <LeadImageAttachment
                    file={file}
                    message={null}
                    {...sharedAttachmentProps}
                  />
                </div>
              ))}
              {groupedFiles.videos.map((file, index) => (
                <div
                  className="LeadFileHistoryAction__item"
                  key={`video_${file.Id}_${index}`}
                >
                  <LeadVideoAttachment
                    file={file}
                    message={null}
                    {...sharedAttachmentProps}
                  />
                </div>
              ))}
            </div>
          )}

          {groupedFiles.audio.length > 0 && (
            <div className="LeadFileHistoryAction__group">
              {groupedFiles.audio.map((file, index) => (
                <div
                  className="LeadFileHistoryAction__item"
                  key={`audio_${file.Id}_${index}`}
                >
                  <LeadAudioMessage
                    messageId={null}
                    file={file}
                    messageType={null}
                    {...sharedAttachmentProps}
                  />
                </div>
              ))}
            </div>
          )}

          {groupedFiles.files.length > 0 && (
            <div className="LeadFileHistoryAction__group">
              {groupedFiles.files.map((file, index) => (
                <div
                  className="LeadFileHistoryAction__item"
                  key={`file_${file.Id}_${index}`}
                >
                  <LeadFileAttachment
                    file={file}
                    message={null}
                    {...sharedAttachmentProps}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
